import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Alert,
} from 'reactstrap'
import { motion } from 'framer-motion'
import { 
  FaArrowLeft, 
  FaTruck, 
  FaUser, 
  FaCheckCircle, 
  FaCalendarAlt, 
  FaPhoneAlt, 
  FaIdCard,
  FaFileAlt,
  FaWeightHanging,
  FaClock
} from 'react-icons/fa'
import { fetchTruckNomination } from '../../store/slices/transportRequestSlice'
import './TransportRequest.scss'

const MyNominations = () => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { nomination, loading } = useSelector(state => state.transportRequest)

  useEffect(() => {
    dispatch(fetchTruckNomination(id))
  }, [dispatch, id])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  }

  if (loading || !nomination) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-3 text-muted">Loading nomination details...</p>
      </div>
    )
  }

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="management-page"
    >
      <Container fluid>
        <div className="page-header d-flex flex-column mb-4">
          <motion.div variants={itemVariants}>
            <Button
              color="link"
              className="p-0 mb-3 text-decoration-none d-inline-flex align-items-center text-primary fw-bold"
              onClick={() => navigate(`/transport-request/${id}`)}
            >
              <FaArrowLeft className="me-2" />
              Return to Request Details
            </Button>
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="page-title neo-text m-0">Truck Nomination</h2>
              <Badge color="success" className="status-badge py-2 px-3">
                <FaCheckCircle className="me-2" />
                Authorized
              </Badge>
            </div>
          </motion.div>
        </div>

        <Row className="g-4">
          <Col lg={8}>
            <motion.div variants={itemVariants}>
              <Card className="aurora-card h-100">
                <CardBody className="p-4">
                  <h5 className="neo-text mb-4 d-flex align-items-center">
                    <FaTruck className="me-3 text-primary" />
                    Vehicle Details
                  </h5>

                  <div className="request-details-page">
                    <Row className="g-4">
                      <Col md={6}>
                        <div className="detail-item">
                          <label className="detail-label">Vehicle Number</label>
                          <p className="detail-value fw-bold h4">{nomination.truck_number}</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item">
                          <label className="detail-label">Vehicle Type</label>
                          <p className="detail-value">{nomination.truck_type}</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item">
                          <label className="detail-label">Payload Capacity</label>
                          <p className="detail-value">
                            {nomination.truck_capacity} {nomination.truck_capacity_unit}
                          </p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item">
                          <label className="detail-label">Model Year</label>
                          <p className="detail-value">{nomination.truck_model_year}</p>
                        </div>
                      </Col>
                    </Row>

                    <h5 className="neo-text mt-5 mb-4 d-flex align-items-center">
                      <FaUser className="me-3 text-primary" />
                      Driver Details
                    </h5>

                    <Row className="g-4">
                      <Col md={6}>
                          <div className="mini-detail">
                            <span className="label"><FaFileAlt className="me-2" /> Request ID</span>
                            <span className="value fw-bold">{nomination.request_number}</span>
                          </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item">
                          <label className="detail-label">Operator Name</label>
                          <p className="detail-value fw-bold">{nomination.driver_name}</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item">
                          <label className="detail-label">Comms Frequency</label>
                          <p className="detail-value">
                            <a href={`tel:${nomination.driver_phone}`} className="text-decoration-none text-primary fw-bold">
                              <FaPhoneAlt className="me-2 small" />
                              {nomination.driver_phone}
                            </a>
                          </p>
                        </div>
                      </Col>
                    </Row>

                    {nomination.notes && (
                      <div className="mt-5">
                        <h5 className="neo-text mb-3 d-flex align-items-center">
                          <FaFileAlt className="me-3 text-primary" />
                          Additional Notes
                        </h5>
                        <div className="glass-effect p-3 rounded-3 border-glass">
                          <p className="text-main mb-0">{nomination.notes}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </motion.div>
          </Col>

          <Col lg={4}>
            <div className="d-flex flex-column gap-4">
              <motion.div variants={itemVariants}>
                <Card className="aurora-card">
                  <CardBody className="p-4">
                    <h5 className="neo-text mb-4">Nomination Status</h5>

                    <div className="detail-item mb-4 bg-transparent border-0 p-0">
                      <label className="detail-label">Execution Phase</label>
                      <div className="mt-2">
                        <Badge color="success" className="status-badge fs-6 px-3 py-2">
                          Synchronized
                        </Badge>
                      </div>
                    </div>

                    <div className="detail-item mb-4 bg-transparent border-0 p-0">
                      <label className="detail-label">Timestamp</label>
                      <p className="detail-value">
                        {new Date(nomination.nomination_date).toLocaleString()}
                      </p>
                    </div>

                    {nomination.contract_loading_id && (
                      <div className="mt-4 pt-4 border-top border-glass">
                        <Alert className="glass-effect border-success bg-transparent mb-0">
                          <div className="d-flex align-items-center mb-2">
                            <FaFileAlt size={20} className="text-success me-3" />
                            <strong className="text-success">Contract Locked</strong>
                          </div>
                          <p className="small text-muted mb-3">Freight allocation has been formally authorized.</p>
                          <Button
                            className="btn-gradient w-100"
                            size="sm"
                            onClick={() => navigate(`/contract-loading/${nomination.contract_loading_id}`)}
                          >
                            View Details
                          </Button>
                        </Alert>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Card className="aurora-card">
                  <CardBody className="p-4">
                    <h5 className="neo-text mb-4 d-flex align-items-center">
                      <FaClock className="me-3 text-primary" />
                      Nomination Timeline
                    </h5>
                    <div className="timeline">
                      <div className="timeline-item">
                        <div className="timeline-marker"></div>
                        <div className="timeline-content">
                          <small>{new Date(nomination.nomination_date).toLocaleString()}</small>
                          <p>Nomination Initiated</p>
                        </div>
                      </div>
                      <div className="timeline-item">
                        <div className="timeline-marker"></div>
                        <div className="timeline-content">
                          <small>{new Date(nomination.approval_date).toLocaleString()}</small>
                          <p>Authorization Verified</p>
                        </div>
                      </div>
                      {nomination.contract_loading_id && (
                        <div className="timeline-item">
                          <div className="timeline-marker"></div>
                          <div className="timeline-content">
                            <small>{new Date(nomination.approval_date).toLocaleString()}</small>
                            <p>Loading Details Generated</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </motion.div>
            </div>
          </Col>
        </Row>
      </Container>
    </motion.div>
  )
}

export default MyNominations
